import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, API_URL, asArray, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FaqsAdmin } from "@/pages/admin/FaqsAdmin";
import { SettingsAdmin } from "@/pages/admin/SettingsAdmin";
import { TemplatesAdmin } from "@/pages/admin/TemplatesAdmin";
import { FilesAdmin } from "@/pages/admin/FilesAdmin";
import { WhatsAppTemplatesManager } from "@/pages/admin/WhatsAppTemplatesManager";
import PacksAdmin from "@/pages/admin/PacksAdmin";
import { Badge } from "@/components/ui/badge";
import {
    Download, Loader2, LogOut, Mail, MessageCircle, RefreshCcw, Search, Trash2, Upload, X,
} from "lucide-react";
import { BRAND } from "@/lib/brand";

const STATUSES = [
    { value: "novo", label: "Novo", color: "bg-blue-100 text-blue-900 ring-blue-200" },
    { value: "contactado", label: "Contactado", color: "bg-amber-100 text-amber-900 ring-amber-200" },
    { value: "convertido", label: "Convertido", color: "bg-emerald-100 text-emerald-900 ring-emerald-200" },
    { value: "perdido", label: "Perdido", color: "bg-stone-200 text-stone-700 ring-stone-300" },
];

const statusMeta = (s) => STATUSES.find((x) => x.value === s) || STATUSES[0];

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("pt-PT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const fmtAnswer = (v) => (v && String(v).trim()) ? v : "—";

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "all", localidade: "", q: "", product: "all" });
    const [selectedId, setSelectedId] = useState(null);
    const selected = useMemo(
        () => leads.find((l) => l.id === selectedId) || null,
        [leads, selectedId]
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status && filters.status !== "all") params.status = filters.status;
            if (filters.product && filters.product !== "all") params.product = filters.product;
            if (filters.localidade.trim()) params.localidade = filters.localidade.trim();
            if (filters.q.trim()) params.q = filters.q.trim();
            const { data } = await api.get("/admin/leads", { params });
            setLeads(asArray(data));
        } catch (err) {
            if (err?.response?.status !== 401) toast.error("Erro a carregar leads.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        if (user) load();
    }, [user, load]);

    const counts = useMemo(() => {
        const c = { total: leads.length, novo: 0, contactado: 0, convertido: 0, perdido: 0 };
        for (const l of leads) c[l.status] = (c[l.status] || 0) + 1;
        return c;
    }, [leads]);

    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center text-stone-500">
                A carregar…
            </div>
        );
    }
    if (user === null) return <Navigate to="/admin/login" replace />;

    const updateLead = async (id, payload) => {
        try {
            const { data } = await api.patch(`/admin/leads/${id}`, payload);
            setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
            toast.success("Atualizado.");
        } catch {
            toast.error("Erro ao atualizar.");
        }
    };

    const deleteLead = async (id) => {
        try {
            await api.delete(`/admin/leads/${id}`);
            setLeads((prev) => prev.filter((l) => l.id !== id));
            setSelectedId(null);
            toast.success("Lead apagada.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Erro a apagar lead.";
            toast.error(typeof detail === "string" ? detail : "Erro a apagar lead.");
        }
    };

    const sendProposal = async (id, mensagem, attachment_ids = []) => {
        try {
            const { data } = await api.post(`/admin/leads/${id}/send-proposal`, { mensagem, attachment_ids });
            toast.success(data.message || "Email enviado.");
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Erro ao enviar.";
            toast.error(typeof detail === "string" ? detail : "Erro ao enviar.");
        }
    };

    const exportCsv = () => {
        const token = getToken();
        const url = `${API_URL}/admin/leads/export`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                if (!r.ok) throw new Error("export failed");
                return r.blob();
            })
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("CSV transferido.");
            })
            .catch(() => toast.error("Não foi possível exportar."));
    };

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-beige-50">
            {/* Top bar */}
            <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <img src={BRAND.logo} alt="" className="h-10 w-10 object-contain" />
                        <div className="leading-tight">
                            <div className="font-serif text-stone-900">{BRAND.name}</div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">
                                Painel · Leads
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="hidden sm:block text-sm text-stone-500 mr-2">{user.email}</span>
                        <Button
                            variant="outline"
                            onClick={handleLogout}
                            className="rounded-full border-stone-200"
                            data-testid="logout-btn"
                        >
                            <LogOut size={16} className="mr-2" /> Sair
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
                <Tabs defaultValue="leads" className="w-full">
                    <TabsList
                        className="bg-white ring-1 ring-stone-200 mb-6 h-auto w-full sm:w-auto justify-start flex overflow-x-auto whitespace-nowrap scrollbar-hide gap-1"
                        data-testid="admin-tabs-list"
                    >
                        <TabsTrigger value="leads" data-testid="tab-leads" className="shrink-0 px-4 py-2">Leads</TabsTrigger>
                        <TabsTrigger value="faqs" data-testid="tab-faqs" className="shrink-0 px-4 py-2">FAQs</TabsTrigger>
                        <TabsTrigger value="templates" data-testid="tab-templates" className="shrink-0 px-4 py-2">Templates</TabsTrigger>
                        <TabsTrigger value="files" data-testid="tab-files" className="shrink-0 px-4 py-2">Ficheiros</TabsTrigger>
                        <TabsTrigger value="packs" data-testid="tab-packs" className="shrink-0 px-4 py-2">Packs</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings" className="shrink-0 px-4 py-2">Definições</TabsTrigger>
                    </TabsList>

                    <TabsContent value="leads" className="space-y-0">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    {[
                        { k: "total", label: "Total" },
                        ...STATUSES.map((s) => ({ k: s.value, label: s.label })),
                    ].map(({ k, label }) => (
                        <div
                            key={k}
                            data-testid={`stat-${k}`}
                            className="bg-white rounded-xl ring-1 ring-stone-200 px-5 py-4"
                        >
                            <div className="text-xs uppercase tracking-[0.18em] text-stone-500">{label}</div>
                            <div className="font-serif text-3xl text-stone-900 mt-1">{counts[k] || 0}</div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl ring-1 ring-stone-200 p-4 md:p-5 mb-6 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input
                            value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                            placeholder="Procurar por nome, email ou telefone…"
                            className="pl-9 bg-stone-50 border-stone-200 h-11 rounded-lg focus-visible:ring-0 focus-visible:border-stone-900"
                            data-testid="filter-q"
                        />
                    </div>
                    <Input
                        value={filters.localidade}
                        onChange={(e) => setFilters({ ...filters, localidade: e.target.value })}
                        placeholder="Localidade"
                        className="md:w-52 bg-stone-50 border-stone-200 h-11 rounded-lg focus-visible:ring-0 focus-visible:border-stone-900"
                        data-testid="filter-localidade"
                    />
                    <Select
                        value={filters.status}
                        onValueChange={(v) => setFilters({ ...filters, status: v })}
                    >
                        <SelectTrigger className="md:w-44 h-11 bg-stone-50 border-stone-200" data-testid="filter-status">
                            <SelectValue placeholder="Estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os estados</SelectItem>
                            {STATUSES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        value={filters.product}
                        onValueChange={(v) => setFilters({ ...filters, product: v })}
                    >
                        <SelectTrigger className="md:w-40 h-11 bg-stone-50 border-stone-200" data-testid="filter-product">
                            <SelectValue placeholder="Produto" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os produtos</SelectItem>
                            <SelectItem value="personal_chef">Personal Chef</SelectItem>
                            <SelectItem value="marmitas">MarFit Meals</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        onClick={load}
                        className="rounded-full border-stone-200 h-11"
                        data-testid="refresh-btn"
                    >
                        <RefreshCcw size={16} className="mr-2" /> Atualizar
                    </Button>
                    <Button
                        onClick={exportCsv}
                        className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 h-11"
                        data-testid="export-csv"
                    >
                        <Download size={16} className="mr-2" /> CSV
                    </Button>
                </div>

                {/* Leads table / list */}
                <div className="bg-white rounded-xl ring-1 ring-stone-200 overflow-hidden">
                    <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-beige-50 text-xs uppercase tracking-[0.18em] text-stone-500">
                        <div className="col-span-3">Nome</div>
                        <div className="col-span-3">Email</div>
                        <div className="col-span-2">Localidade</div>
                        <div className="col-span-2">Data</div>
                        <div className="col-span-2">Estado</div>
                    </div>
                    {loading ? (
                        <div className="px-5 py-12 text-center text-stone-500">A carregar…</div>
                    ) : leads.length === 0 ? (
                        <div className="px-5 py-12 text-center text-stone-500" data-testid="no-leads">
                            Sem leads correspondentes.
                        </div>
                    ) : (
                        <ul>
                            {leads.map((l) => {
                                const s = statusMeta(l.status);
                                return (
                                    <li
                                        key={l.id}
                                        onClick={() => setSelectedId(l.id)}
                                        data-testid={`lead-row-${l.id}`}
                                        className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-3 px-5 py-4 border-t border-stone-100 hover:bg-beige-50/50 cursor-pointer transition-colors"
                                    >
                                        <div className="md:col-span-3 font-medium text-stone-900 flex items-center gap-2">
                                            {l.nome}
                                            {l.product === "marmitas" && (
                                                <span
                                                    data-testid={`lead-product-badge-${l.id}`}
                                                    className="inline-flex text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200"
                                                    title="MarFit Meals"
                                                >
                                                    MarFit
                                                </span>
                                            )}
                                        </div>
                                        <div className="md:col-span-3 text-sm text-stone-600 break-all">{l.email}</div>
                                        <div className="md:col-span-2 text-sm text-stone-600">{l.localidade}</div>
                                        <div className="md:col-span-2 text-sm text-stone-500">{fmtDate(l.created_at)}</div>
                                        <div className="md:col-span-2">
                                            <span className={`inline-flex text-xs px-2.5 py-1 rounded-full ring-1 ${s.color}`}>
                                                {s.label}
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                    </TabsContent>

                    <TabsContent value="faqs">
                        <FaqsAdmin />
                    </TabsContent>

                    <TabsContent value="templates">
                        <TemplatesAdmin />
                    </TabsContent>

                    <TabsContent value="files">
                        <FilesAdmin />
                    </TabsContent>

                    <TabsContent value="packs">
                        <PacksAdmin />
                    </TabsContent>

                    <TabsContent value="settings">
                        <SettingsAdmin />
                    </TabsContent>
                </Tabs>
            </main>

            {selected && (
                <LeadDrawer
                    lead={selected}
                    onClose={() => setSelectedId(null)}
                    onUpdate={(payload) => updateLead(selected.id, payload)}
                    onSend={(msg) => sendProposal(selected.id, msg)}
                    onDelete={() => deleteLead(selected.id)}
                />
            )}
        </div>
    );
}

const LeadDrawer = ({ lead, onClose, onUpdate, onSend, onDelete }) => {
    const [notes, setNotes] = useState(lead.notes || "");
    const [status, setStatus] = useState(lead.status);
    const [proposalMsg, setProposalMsg] = useState("");
    const [sendOpen, setSendOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [availablePdfs, setAvailablePdfs] = useState([]);
    const [selectedAttIds, setSelectedAttIds] = useState([]);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const newPdfInputRef = useRef(null);
    const [waOpen, setWaOpen] = useState(false);
    const [waMsg, setWaMsg] = useState("");
    const [waSelectedIds, setWaSelectedIds] = useState([]);
    const [waUploading, setWaUploading] = useState(false);
    const waPdfInputRef = useRef(null);
    const [waTemplates, setWaTemplates] = useState([]);
    const [waTplMgrOpen, setWaTplMgrOpen] = useState(false);

    useEffect(() => {
        setNotes(lead.notes || "");
        setStatus(lead.status);
    }, [lead]);

    useEffect(() => {
        if (!sendOpen && !waOpen) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/attachments", { params: { kind: "proposal_pdf" } });
                setAvailablePdfs(data || []);
            } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Failed to load attachments for proposal:", err?.message || err);
                }
            }
        })();
    }, [sendOpen, waOpen]);

    // Interpolate {nome}, {primeiro_nome}, {localidade}, {email}, {telefone} in a template body
    const interpolate = (body) => {
        const firstName = (lead.nome || "").split(" ")[0] || "";
        return (body || "")
            .replace(/\{primeiro_nome\}/g, firstName)
            .replace(/\{nome\}/g, lead.nome || "")
            .replace(/\{localidade\}/g, lead.localidade || "")
            .replace(/\{email\}/g, lead.email || "")
            .replace(/\{telefone\}/g, lead.telefone || "");
    };

    // Pre-fill WhatsApp message when opening + load templates
    useEffect(() => {
        if (waOpen) {
            (async () => {
                try {
                    const { data } = await api.get("/admin/whatsapp-templates");
                    const list = asArray(data);
                    setWaTemplates(list);
                    // Only prefill if empty (respect user edits after re-open)
                    setWaMsg((prev) => {
                        if (prev) return prev;
                        const first = list[0];
                        return first ? interpolate(first.body) : "";
                    });
                } catch (err) {
                    if (process.env.NODE_ENV !== "production") {
                        console.warn("Failed to load WA templates:", err?.message || err);
                    }
                }
            })();
        } else {
            setWaMsg("");
            setWaSelectedIds([]);
        }
    }, [waOpen]);

    const s = statusMeta(status);
    const waUrl = `https://wa.me/${(lead.telefone || "").replace(/[^0-9]/g, "")}`;
    const mailto = `mailto:${lead.email}`;

    const handleSend = async () => {
        setSending(true);
        await onSend(proposalMsg, selectedAttIds);
        setSending(false);
        setSendOpen(false);
        setProposalMsg("");
        setSelectedAttIds([]);
    };

    const toggleAtt = (id) => {
        setSelectedAttIds((arr) =>
            arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]
        );
    };

    const handleNewPdfPick = () => newPdfInputRef.current?.click();

    const handleNewPdfUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingPdf(true);
        const uploadedIds = [];
        for (const f of files) {
            if (f.type !== "application/pdf") {
                toast.error(`${f.name}: apenas PDFs são permitidos aqui.`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/admin/attachments", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                if (data?.id) {
                    uploadedIds.push(data.id);
                    setAvailablePdfs((arr) => [data, ...arr]);
                }
            } catch (err) {
                const detail = err?.response?.data?.detail || err?.message || "falha desconhecida";
                toast.error(`Erro a carregar ${f.name}: ${detail}`);
            }
        }
        if (uploadedIds.length > 0) {
            // Auto-select the newly uploaded PDFs for this proposal
            setSelectedAttIds((arr) => [...new Set([...arr, ...uploadedIds])]);
            toast.success(`${uploadedIds.length} PDF(s) carregado(s) e pré-selecionado(s).`);
        }
        setUploadingPdf(false);
        if (newPdfInputRef.current) newPdfInputRef.current.value = "";
    };

    // ---------- WhatsApp handlers ----------
    const toggleWaAtt = (id) => {
        setWaSelectedIds((arr) =>
            arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]
        );
    };

    const handleWaPdfPick = () => waPdfInputRef.current?.click();

    const handleWaPdfUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setWaUploading(true);
        const uploadedIds = [];
        for (const f of files) {
            if (f.type !== "application/pdf") {
                toast.error(`${f.name}: apenas PDFs são permitidos.`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/admin/attachments", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                if (data?.id) {
                    uploadedIds.push(data.id);
                    setAvailablePdfs((arr) => [data, ...arr]);
                }
            } catch (err) {
                const detail = err?.response?.data?.detail || err?.message;
                toast.error(`Erro a carregar ${f.name}: ${detail}`);
            }
        }
        if (uploadedIds.length > 0) {
            setWaSelectedIds((arr) => [...new Set([...arr, ...uploadedIds])]);
            toast.success(`${uploadedIds.length} PDF(s) carregado(s) e selecionado(s).`);
        }
        setWaUploading(false);
        if (waPdfInputRef.current) waPdfInputRef.current.value = "";
    };

    // Base for share URLs. Falls back to empty (→ same-origin) when the env
    // var is not defined at build time, so /api/attachments/... routes still
    // reach the backend through nginx.
    const backendBase = (process.env.REACT_APP_BACKEND_URL || "")
        .replace(/\/+$/, "")
        .replace(/^undefined$/, "");

    /**
     * Fetch selected PDFs as File objects (via the public share endpoint, which
     * doesn't require auth) so we can hand them to the native Web Share API on
     * mobile — where WhatsApp accepts them as real attachments.
     */
    const fetchSelectedFiles = async () => {
        const files = [];
        for (const id of waSelectedIds) {
            try {
                const found = availablePdfs.find((a) => a.id === id);
                let token = found?.public_token;
                if (!token) {
                    const { data } = await api.get(`/admin/attachments/${id}/share-token`);
                    token = data?.public_token;
                }
                if (!token) continue;
                const url = `${backendBase}/api/attachments/public/${token}`;
                const resp = await fetch(url);
                if (!resp.ok) continue;
                const blob = await resp.blob();
                const name = found?.filename || "proposta.pdf";
                files.push(new File([blob], name, { type: blob.type || "application/pdf" }));
            } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Failed to fetch file for sharing", id, err);
                }
            }
        }
        return files;
    };

    const buildPublicLinks = async () => {
        const links = [];
        for (const id of waSelectedIds) {
            try {
                // Try to mint a per-lead share so the URL includes the lead's name
                // (e.g. /p/proposta-marta-silva) and we track opens per-lead.
                try {
                    const { data: share } = await api.post(
                        `/admin/attachments/${id}/shares`, { lead_id: lead.id },
                    );
                    if (share?.slug) {
                        links.push(`${backendBase}/api/p/${share.slug}`);
                        continue;
                    }
                } catch (shareErr) {
                    if (process.env.NODE_ENV !== "production") {
                        console.warn("Could not mint per-lead share, falling back:", shareErr?.message);
                    }
                }

                // Fallback: global attachment slug
                const found = availablePdfs.find((a) => a.id === id);
                if (found?.slug) {
                    links.push(`${backendBase}/api/p/${found.slug}`);
                    continue;
                }
                let token = found?.public_token;
                if (!token) {
                    const { data } = await api.get(`/admin/attachments/${id}/share-token`);
                    token = data?.public_token;
                }
                if (token) links.push(`${backendBase}/api/attachments/public/${token}`);
            } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Failed to resolve share token", id, err);
                }
            }
        }
        return links;
    };

    const openInWhatsApp = async () => {
        const phoneDigits = (lead.telefone || "").replace(/\D/g, "");
        if (!phoneDigits) {
            toast.error("Este lead não tem número de telefone válido.");
            return;
        }

        const body = waMsg.trim();
        // When the template contains {link_proposta}, the admin explicitly
        // wants a LINK-ONLY message (no native file attachment). Skip Path A
        // (Web Share) and go straight to the wa.me link flow so the URL is
        // substituted in-place and the PDF isn't also sent as an attachment.
        const hasPlaceholder = /\{link_proposta\}/.test(body);
        if (hasPlaceholder && waSelectedIds.length === 0) {
            toast.error("Seleciona um PDF em baixo para gerar o link da proposta.");
            return;
        }

        // ---- Path A: Native Web Share (mobile) — attaches files for real ----
        // Requires: navigator.canShare({files}) support, secure context, user gesture.
        if (!hasPlaceholder && waSelectedIds.length > 0 && navigator.canShare) {
            try {
                const files = await fetchSelectedFiles();
                if (files.length > 0 && navigator.canShare({ files })) {
                    await navigator.share({
                        title: "Proposta — Synara Alvarenga",
                        text: body,
                        files,
                    });
                    setWaOpen(false);
                    toast.success("Partilhado via WhatsApp com o PDF anexado.");
                    return;
                }
            } catch (err) {
                // User cancelled the share sheet — just abort silently.
                if (err?.name === "AbortError") {
                    return;
                }
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Web Share failed, falling back to link:", err);
                }
            }
        }

        // ---- Path B: Fallback — text-only WhatsApp with a public link ----
        const links = await buildPublicLinks();
        let fullBody = body;
        const primaryLink = links[0] || "";
        if (hasPlaceholder) {
            // Substitute in place and never append twice.
            fullBody = fullBody.replace(/\{link_proposta\}/g, primaryLink);
        } else if (links.length > 0) {
            fullBody += "\n\n" + (links.length === 1 ? "Proposta: " : "Anexos:\n") + links.join("\n");
        }
        const url = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(fullBody)}`;
        window.open(url, "_blank");
        setWaOpen(false);
        toast.success(
            hasPlaceholder
                ? "WhatsApp aberto com o link personalizado."
                : waSelectedIds.length > 0
                    ? "WhatsApp aberto. O PDF vai como link (não fica anexado no desktop)."
                    : "WhatsApp aberto numa nova aba."
        );
    };

    const q = lead.quiz || {};

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-drawer">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl text-stone-900 pr-8 flex items-center gap-2">
                        {lead.nome}
                        {lead.product === "marmitas" && (
                            <span
                                data-testid="drawer-product-badge"
                                className="inline-flex text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200 font-sans"
                            >
                                MarFit
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Detalhes do lead, atualização de estado, notas internas e envio de proposta.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-2">
                    {/* Contact + actions */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex text-xs px-2.5 py-1 rounded-full ring-1 ${s.color}`}>{s.label}</span>
                        <span className="text-xs text-stone-500">{fmtDate(lead.created_at)}</span>
                        <div className="ml-auto flex gap-2">
                            <a href={waUrl} target="_blank" rel="noreferrer">
                                <Button variant="outline" size="sm" className="rounded-full border-stone-200" data-testid="lead-wa">
                                    <MessageCircle size={14} className="mr-1.5" /> WhatsApp
                                </Button>
                            </a>
                            <a href={mailto}>
                                <Button variant="outline" size="sm" className="rounded-full border-stone-200" data-testid="lead-mail">
                                    <Mail size={14} className="mr-1.5" /> Email
                                </Button>
                            </a>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        <Field label="Telefone" value={lead.telefone} />
                        <Field label="Email" value={lead.email} />
                        <Field label="Localidade" value={lead.localidade} />
                        <Field label="Estado email" value={lead.last_email_status || "—"} />
                        <Field label="Proposta enviada" value={fmtDate(lead.proposal_sent_at)} />
                    </div>

                    {lead.mensagem && (
                        <div>
                            <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Mensagem</Label>
                            <div className="mt-1.5 text-stone-700 text-sm whitespace-pre-wrap bg-beige-50 rounded-lg p-3 ring-1 ring-stone-200">
                                {lead.mensagem}
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Respostas do questionário</Label>
                        {lead.product === "marmitas" ? (
                            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                <Field label="Pack" value={q.pack ? `${q.pack} marmitas` : "—"} />
                                <Field label="Refeições/dia" value={fmtAnswer(q.refeicoes_dia)} />
                                <Field label="Estilo alimentar" value={fmtAnswer(q.preferencia_alimentar)} />
                                <Field label="Alergias" value={fmtAnswer(q.alergias)} />
                                <Field label="Dia de entrega" value={fmtAnswer(q.entrega_dia)} />
                            </div>
                        ) : (
                            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                <Field label="Objetivo" value={fmtAnswer(q.objetivo)} />
                                <Field label="Pessoas" value={fmtAnswer(q.pessoas)} />
                                <Field label="Frequência" value={fmtAnswer(q.frequencia)} />
                                <Field label="Cozinha" value={fmtAnswer(q.cozinha)} />
                                <Field label="Dificuldade" value={fmtAnswer(q.dificuldade)} />
                            </div>
                        )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Estado</Label>
                            <Select value={status} onValueChange={(v) => { setStatus(v); onUpdate({ status: v }); }}>
                                <SelectTrigger className="mt-1.5 h-11 bg-stone-50 border-stone-200" data-testid="drawer-status">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((x) => (
                                        <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <div className="w-full grid grid-cols-2 gap-2">
                                <Button
                                    onClick={() => setSendOpen(true)}
                                    className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 h-11 px-3"
                                    data-testid="drawer-send-proposal"
                                >
                                    <Mail size={16} className="mr-1.5" /> Email
                                </Button>
                                <Button
                                    onClick={() => setWaOpen(true)}
                                    className="rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white h-11 px-3"
                                    data-testid="drawer-send-whatsapp"
                                >
                                    <MessageCircle size={16} className="mr-1.5" /> WhatsApp
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Notas internas</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            onBlur={() => notes !== (lead.notes || "") && onUpdate({ notes })}
                            placeholder="Notas privadas sobre este lead…"
                            className="mt-1.5 bg-stone-50 border-stone-200 focus-visible:ring-0 focus-visible:border-stone-900 rounded-lg min-h-[100px]"
                            data-testid="drawer-notes"
                        />
                        <div className="text-[11px] text-stone-400 mt-1">Guarda automaticamente ao sair do campo.</div>
                    </div>
                </div>

                <DialogFooter className="sm:justify-between gap-2">
                    <Button
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteOpen(true)}
                        data-testid="drawer-delete-lead"
                    >
                        <Trash2 size={16} className="mr-2" /> Apagar lead
                    </Button>
                    <Button variant="ghost" onClick={onClose}>
                        <X size={16} className="mr-2" /> Fechar
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Confirm delete lead */}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent data-testid="delete-lead-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Apagar lead de {lead.nome}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação é permanente. A lead, respostas do quiz e notas serão removidas da base de dados.
                            Os anexos partilhados com este lead deixam de contabilizar aberturas.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async (e) => {
                                e.preventDefault();
                                setDeleting(true);
                                try {
                                    await onDelete();
                                    setDeleteOpen(false);
                                } finally {
                                    setDeleting(false);
                                }
                            }}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                            data-testid="drawer-delete-confirm"
                        >
                            {deleting ? (
                                <><Loader2 size={16} className="mr-2 animate-spin" /> A apagar…</>
                            ) : (
                                <><Trash2 size={16} className="mr-2" /> Apagar definitivamente</>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm send proposal */}
            <Dialog open={sendOpen} onOpenChange={setSendOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Enviar proposta a {lead.nome}</DialogTitle>
                        <DialogDescription>
                            O lead receberá um email com o PDF da proposta em anexo.
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm text-stone-600">
                        Será enviado um email para <strong>{lead.email}</strong>{selectedAttIds.length ? ` com ${selectedAttIds.length} anexo(s) do arquivo.` : "."}
                    </p>

                    {/* Choose attachments */}
                    <div>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">
                                Anexar PDFs
                            </Label>
                            <input
                                ref={newPdfInputRef}
                                type="file"
                                accept="application/pdf"
                                multiple
                                className="hidden"
                                onChange={handleNewPdfUpload}
                                data-testid="proposal-new-pdf-input"
                            />
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleNewPdfPick}
                                disabled={uploadingPdf}
                                className="rounded-full text-xs h-8 border-stone-200"
                                data-testid="proposal-upload-new-pdf"
                            >
                                {uploadingPdf ? (
                                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                                ) : (
                                    <Upload size={14} className="mr-1.5" />
                                )}
                                {uploadingPdf ? "A carregar…" : "Carregar novo PDF"}
                            </Button>
                        </div>

                        {availablePdfs.length === 0 ? (
                            <p className="text-xs text-stone-500 mt-2 bg-stone-50 ring-1 ring-stone-200 rounded-lg p-3">
                                Ainda não tens PDFs no arquivo. Carrega um novo aqui em cima — ficará guardado para futuras propostas.
                            </p>
                        ) : (
                            <div className="mt-1.5 max-h-32 overflow-y-auto bg-stone-50 ring-1 ring-stone-200 rounded-lg p-2 space-y-1">
                                {availablePdfs.map((p) => (
                                    <label
                                        key={p.id}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedAttIds.includes(p.id)}
                                            onChange={() => toggleAtt(p.id)}
                                            className="accent-stone-900"
                                            data-testid={`pdf-checkbox-${p.id}`}
                                        />
                                        <span className="flex-1 truncate text-stone-700">{p.filename}</span>
                                        <span className="text-[10px] text-stone-400">
                                            {(p.size_bytes / 1024).toFixed(0)} KB
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Mensagem adicional (opcional)</Label>
                        <Textarea
                            value={proposalMsg}
                            onChange={(e) => setProposalMsg(e.target.value)}
                            placeholder="Adicione uma mensagem personalizada…"
                            className="mt-1.5 bg-stone-50 border-stone-200 rounded-lg min-h-[100px]"
                            data-testid="proposal-msg"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setSendOpen(false)}>Cancelar</Button>
                        <Button
                            onClick={handleSend}
                            disabled={sending}
                            className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50"
                            data-testid="confirm-send"
                        >
                            {sending ? "A enviar…" : "Enviar agora"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ---------- WhatsApp modal ---------- */}
            <Dialog open={waOpen} onOpenChange={setWaOpen}>
                <DialogContent className="max-w-lg" data-testid="whatsapp-dialog">
                    <DialogHeader>
                        <DialogTitle>Responder por WhatsApp</DialogTitle>
                        <DialogDescription>
                            Para: <strong>{lead.nome}</strong> — {lead.telefone || "sem telefone"}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {waTemplates.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                    <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">
                                        Template pré-definido
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={() => setWaTplMgrOpen(true)}
                                        className="text-xs text-stone-500 hover:text-stone-900 underline underline-offset-2"
                                        data-testid="wa-manage-templates"
                                    >
                                        Gerir templates
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {waTemplates.map((tpl) => (
                                        <button
                                            key={tpl.id}
                                            type="button"
                                            onClick={() => setWaMsg(interpolate(tpl.body))}
                                            className="text-xs px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-900 hover:text-stone-50 border border-stone-200 transition-colors"
                                            data-testid={`wa-tpl-${tpl.id}`}
                                        >
                                            {tpl.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Mensagem</Label>
                            <Textarea
                                value={waMsg}
                                onChange={(e) => setWaMsg(e.target.value)}
                                className="mt-1.5 bg-stone-50 border-stone-200 rounded-lg min-h-[120px]"
                                data-testid="whatsapp-message"
                            />
                            <div className="text-[11px] text-stone-500 mt-1">
                                Os anexos que selecionares aparecem como links no fim da mensagem.
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">
                                    Anexar PDFs (opcional)
                                </Label>
                                <input
                                    ref={waPdfInputRef}
                                    type="file"
                                    accept="application/pdf"
                                    multiple
                                    className="hidden"
                                    onChange={handleWaPdfUpload}
                                    data-testid="whatsapp-new-pdf-input"
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleWaPdfPick}
                                    disabled={waUploading}
                                    className="rounded-full text-xs h-8 border-stone-200"
                                    data-testid="whatsapp-upload-new-pdf"
                                >
                                    {waUploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                                    {waUploading ? "A carregar…" : "Novo PDF"}
                                </Button>
                            </div>

                            {availablePdfs.length === 0 ? (
                                <p className="text-xs text-stone-500 mt-2 bg-stone-50 ring-1 ring-stone-200 rounded-lg p-3">
                                    Sem PDFs no arquivo. Carrega um novo aqui em cima.
                                </p>
                            ) : (
                                <div className="mt-1.5 max-h-32 overflow-y-auto bg-stone-50 ring-1 ring-stone-200 rounded-lg p-2 space-y-1">
                                    {availablePdfs.map((p) => (
                                        <label
                                            key={p.id}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer text-sm"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={waSelectedIds.includes(p.id)}
                                                onChange={() => toggleWaAtt(p.id)}
                                                className="accent-[#25D366]"
                                                data-testid={`wa-pdf-checkbox-${p.id}`}
                                            />
                                            <span className="flex-1 truncate text-stone-700">{p.filename}</span>
                                            <span className="text-[10px] text-stone-400">
                                                {(p.size_bytes / 1024).toFixed(0)} KB
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {waSelectedIds.length > 0 && (
                                <div className="mt-2 text-[11px] text-stone-500 bg-amber-50 ring-1 ring-amber-200 rounded-lg p-2.5 leading-relaxed">
                                    <strong className="text-amber-800">Nota:</strong>{" "}
                                    <span className="hidden md:inline">No desktop, o WhatsApp Web abre apenas com a mensagem — o PDF vai como link clicável (limitação do próprio WhatsApp).</span>
                                    <span className="md:hidden">No telemóvel o PDF é anexado diretamente. No desktop vai como link clicável.</span>
                                    <span className="block mt-1">Para anexar o ficheiro fisicamente, usa esta função a partir do <strong>telemóvel</strong>.</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setWaOpen(false)}>Cancelar</Button>
                        <Button
                            onClick={openInWhatsApp}
                            className="rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white"
                            data-testid="whatsapp-open"
                        >
                            <MessageCircle size={16} className="mr-1.5" /> Abrir no WhatsApp
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* WhatsApp templates manager (nested modal) */}
            <WhatsAppTemplatesManager
                open={waTplMgrOpen}
                onOpenChange={setWaTplMgrOpen}
                onChanged={async () => {
                    try {
                        const { data } = await api.get("/admin/whatsapp-templates");
                        setWaTemplates(asArray(data));
                    } catch (err) {
                        if (process.env.NODE_ENV !== "production") {
                            console.warn("Failed to refresh WA templates:", err);
                        }
                    }
                }}
            />
        </Dialog>
    );
};

const Field = ({ label, value }) => (
    <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500">{label}</div>
        <div className="text-stone-900 text-sm mt-0.5 break-words">{value || "—"}</div>
    </div>
);
